import React from 'react';
import { AuditRemediationResult, Page, NavParams } from '@types';
import { md, Section, Disclaimer, ICONS, formatTimestamp } from './ReportShared';
import RiskChart from '../RiskChart';

interface AuditRemediationDisplayProps {
    result: AuditRemediationResult;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
}

const AuditRemediationDisplay: React.FC<AuditRemediationDisplayProps> = ({ result, onNavigate }) => {
    // Prepare data for the RiskChart (Radar)
    const chartData = (result.riskFactors || []).map(rf => ({
        factor: rf.factor,
        score: rf.score || 0
    }));

    return (
        <div className="space-y-8 animate-fade-in">
            {/* IntelOptics Branding Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-slate-900 pb-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">IntellOptics Remediation</span>
                        <span className="text-slate-500 font-bold text-[10px] tracking-widest uppercase">Pathway ID: {result.remediationId || 'UNSET'}</span>
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase">
                        {result.findingTitle}
                    </h2>
                    <div className="text-sm text-slate-500 font-medium mt-1 uppercase tracking-wider">
                        Strategic Remediation Pathway Blueprint
                    </div>
                </div>
                <div className="flex flex-col md:items-end">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Compliance Standard</div>
                    <div className="text-sm font-bold text-slate-700">
                        {result.metadata?.standards?.join(' / ') || 'General Protocol'}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">
                        Priority Level: <span className={`font-bold tracking-tighter ${
                            result.priority === 'Critical' ? 'text-red-600' : 
                            result.priority === 'High' ? 'text-orange-600' : 'text-blue-600'
                        }`}>{result.priority.toUpperCase()}</span>
                    </div>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Estimated Effort</span>
                    <span className="text-xl font-black text-slate-800">{result.estimatedEffort}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Resource Req</span>
                    <span className="text-xl font-black text-slate-800">{result.resourceIntensity}</span>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center shadow-sm">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Remediation ID</span>
                    <span className="text-xl font-black text-slate-800 font-mono text-xs">{result.remediationId}</span>
                </div>
            </div>

            {/* Executive Summary & Risk Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <Section title="Executive Overview">
                        <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(result.executiveSummary) }} />
                    </Section>
                    
                    <Section title="Compliance Alignment">
                        <div className="flex flex-wrap gap-2">
                            {result.metadata?.standards?.map((std: string, i: number) => (
                                <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded text-xs font-bold uppercase tracking-wider">
                                    {std}
                                </span>
                            ))}
                        </div>
                        <div className="mt-4 prose prose-sm text-slate-600 italic">
                            This pathway is aligned with recognized global framework controls for strategic operational resiliency.
                        </div>
                    </Section>
                </div>

                <div className="bg-slate-950 rounded-2xl p-8 shadow-2xl border border-slate-800 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-48 w-48 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    </div>
                    <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-8 flex items-center gap-2">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
                        Risk Factor Visualization
                    </h3>
                    <div className="h-80 flex items-center justify-center relative">
                        {/* Subtle background glow for the chart */}
                        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 to-transparent rounded-full blur-3xl opacity-30"></div>
                        <RiskChart data={chartData} minimal={true} />
                    </div>
                    <div className="mt-8 border-t border-slate-900 pt-4 text-[10px] font-mono text-slate-600 leading-tight">
                        SYSTEM ANALYTICS: CALIBRATED TO {result.priority.toUpperCase()} PRIORITY VECTOR<br/>
                        DATASET: INTELOPTICS-SRM-STABLE-{new Date().getFullYear()}
                    </div>
                </div>
            </div>

            {/* Specialized Action Plan */}
            <Section title="Remediation Pathway Plan">
                <div className="space-y-4">
                    {result.pathwayActions.map((action: any, i: number) => (
                        <div key={i} className="flex gap-4 p-5 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
                            <div className="flex-shrink-0 w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center font-black text-sm">
                                {i + 1}
                            </div>
                            <div className="flex-grow">
                                <h4 className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors uppercase tracking-tight">{action.step}</h4>
                                <div className="mt-2 prose prose-sm text-slate-600" dangerouslySetInnerHTML={{ __html: md.render(action.description) }} />
                                <div className="mt-3 flex items-center gap-4">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Complexity: <span className="text-slate-700">{action.complexity}</span></span>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Validation: <span className="text-slate-700">{action.validationMethod}</span></span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Technical Metadata (Terminal View) */}
            <Section title="Technical Metadata Annex">
                <div className="bg-slate-900 rounded-xl p-5 font-mono text-[11px] text-emerald-500/80 leading-relaxed border border-slate-700 shadow-inner overflow-x-auto">
                    <div className="text-slate-500 mb-2">// INTELOPTICS CRYPTOGRAPHIC VERIFICATION & SYSTEM HANDLER TAGS</div>
                    <div>PATHWAY_ID: {result.remediationId || 'UNSET'}</div>
                    <div>TIMESTAMP: {formatTimestamp(result.timestamp) || 'DATA_INCOMPLETE'}</div>
                    <div>PROTOCOL: {result.metadata?.protocolId || 'INTEL-REMED-LEGACY'}</div>
                    <div>SYSTEM_HASH: {result.metadata?.systemHash || 'BLOCK_PENDING'}</div>
                    <div className="mt-2 text-indigo-400"># REGULATORY_CONTROLS_IDENTIFIED:</div>
                    {(result.metadata?.standards || []).map((s: string, i: number) => <div key={i} className="pl-4">- {s}</div>)}
                    <div className="mt-4 text-slate-500">
                        &gt; System check complete. No integrity violations detected.
                        <span className="inline-block w-2 h-4 bg-emerald-500 ml-1 animate-pulse align-middle"></span>
                    </div>
                </div>
            </Section>

            {/* Expert Consultation Offer */}
            {result.expertConsultation && (
                <div className="bg-indigo-600 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl group-hover:bg-white/20 transition-all duration-700"></div>
                    <div className="relative z-10">
                        <h3 className="text-2xl font-black mb-3 tracking-tight">{result.expertConsultation.title}</h3>
                        <p className="text-indigo-100 max-w-2xl mb-6 font-medium leading-relaxed">
                            {result.expertConsultation.description}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button 
                                onClick={() => onNavigate?.({ page: 'contact' })}
                                className="bg-white text-indigo-700 hover:bg-indigo-50 px-8 py-3 rounded-xl font-black transition-all shadow-xl hover:-translate-y-1 active:translate-y-0"
                            >
                                {result.expertConsultation.callToAction}
                            </button>
                            <div className="flex flex-col justify-center">
                                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300">Consultation Status</span>
                                <span className="text-xs font-bold text-white italic">Third-party specialized assessment required. Additional costs apply.</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mandatory Liability Disclaimer */}
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 bg-red-600 h-full"></div>
                <div className="flex items-start gap-4">
                    <div className="mt-1 text-red-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    </div>
                    <div>
                        <h4 className="font-black text-red-900 uppercase tracking-widest text-xs mb-2">Legal Liability Notice & Strategic Disclaimer</h4>
                        <div className="prose prose-sm text-red-800/80 font-medium leading-relaxed" 
                             dangerouslySetInnerHTML={{ __html: md.render(result.legalDisclaimer || 'Strategic advice only.') }} />
                    </div>
                </div>
            </div>

            <Disclaimer />
        </div>
    );
};

export default AuditRemediationDisplay;
